import { formatDayMonth } from "../../api/dates";
import type { PlanOptions, PlannerContext, PlannerCount, Point } from "../../api/routing";
import type { SiteFilterQuery } from "../../api/sites";

/** The one-line summaries the planner's collapsed steps show. Kept apart from
 * the components so the wording can be read, and tested, as sentences. */

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "16 sites · 1 status · Southern Peninsula". The count is the server's
 * (what "Plan the routes" would take); until it arrives the line says so
 * rather than showing a number that is not yet true. */
export function sitesSummary(
  filter: SiteFilterQuery,
  context: PlannerContext,
  count: PlannerCount | undefined,
): string {
  const parts: string[] = [];
  if (count === undefined) {
    parts.push("Counting sites…");
  } else if (count.located < count.sites) {
    parts.push(`${plural(count.sites, "site")} (${count.located} located)`);
  } else {
    parts.push(plural(count.sites, "site"));
  }
  parts.push(filter.status.length === 0 ? "any status" : plural(filter.status.length, "status", "statuses"));
  const cluster = context.filters.clusters.find((c) => String(c.id) === filter.cluster_id);
  parts.push(cluster ? cluster.name : "every cluster");
  const provider = context.filters.providers.find((p) => String(p.id) === filter.provider_id);
  if (provider) {
    parts.push(provider.name);
  }
  return parts.join(" · ");
}

/** "Skakuza yard, Retreat · no meeting point". */
export function startSummary(origin: Point | null, meeting: Point | null): string {
  return `${origin ? origin.label : "No depot yet"} · ${meeting ? `meet at ${meeting.label}` : "no meeting point"}`;
}

/** "07:30–16:00 · 60 min on site · max 6 · from 28 Sep". */
export function daySummary(options: PlanOptions): string {
  return [
    `${options.day_start}–${options.day_end}`,
    `${options.on_site_minutes} min on site`,
    options.max_per_day === null ? "no daily cap" : `max ${options.max_per_day}`,
    options.first_day ? `from ${formatDayMonth(options.first_day)}` : "no first day set",
  ].join(" · ");
}
